import React, { useEffect, useState, useCallback } from "react";
import { attendanceAPI } from "../services/api";
import { PageLoader } from "../components/common/LoadingSpinner";
import {
  STATUS_MAP,
  SUMMARY_ITEMS,
  buildAttendanceCalendar,
  formatHistoryTime,
  formatHours,
  getPeriodRange,
  toLocalDateKey,
} from "../utils/attendanceHistory";

function DayCell({ day, status, flags = {}, isCurrentMonth, isAdjacentMonth, isToday, onClick }) {
  if (!isCurrentMonth) return <div className="aspect-square"></div>;

  const statusBarColor = STATUS_MAP[status]?.color || "bg-gray-200/60";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-between py-2 rounded-xl transition-all aspect-square relative ${
        isToday
          ? "bg-gray-900 border border-gray-900 shadow-md scale-105 z-10"
          : "bg-white hover:bg-gray-50"
      } active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/20`}
    >
      {flags.wfh && (
        <span
          className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full border ${
            isToday ? "bg-lime-300 border-gray-900" : "bg-lime-500 border-white"
          }`}
          title="WFH"
        />
      )}

      <span
        className={`text-sm font-semibold tracking-tight mt-0.5 ${
          isToday ? "text-white" : isAdjacentMonth ? "text-gray-400" : "text-gray-800"
        }`}
        title={isAdjacentMonth ? "Tanggal dari bulan sebelumnya, tetap dihitung dalam periode ini" : undefined}
      >
        {day}
      </span>

      <div className="w-5 mb-0.5">
        <div className={`h-[3px] rounded-full ${isToday && status ? "bg-white" : statusBarColor}`} />
      </div>
    </button>
  );
}

function GreetingHeader() {
  return (
    <div className="bg-primary text-white pt-10 pb-32 px-6 relative overflow-hidden">
      <div className="absolute top-[30%] -left-40 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative z-10 max-w-md mx-auto">
        <span className="inline-flex bg-white/15 backdrop-blur-xl text-white text-[9px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-white/10 shadow-sm">
          Riwayat
        </span>
        <h1 className="text-2xl font-black tracking-tight mt-3">Attendance History</h1>
        <p className="text-white/70 text-xs mt-0.5 font-semibold">Rekap absensi bulanan</p>
      </div>
    </div>
  );
}

function SummaryStats({ stats }) {
  return (
    <div className="bg-white rounded-[24px] p-6 border border-gray-100 shadow-sm max-w-md mx-auto w-full">
      <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
        STATUS LEGEND & SUMMARY
      </h3>

      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
        {SUMMARY_ITEMS.map((item) => (
          <div key={item.key} className="flex items-center justify-between py-0.5">
            <div className="flex items-center gap-2.5">
              <span className={`w-2.5 h-2.5 rounded-full ${item.dotColor} shrink-0`} />
              <span className="text-sm font-medium text-gray-600">{item.label}</span>
            </div>
            <span className="text-xs font-bold text-gray-800 bg-gray-50 px-2.5 py-0.5 rounded-full border border-gray-100 min-w-[28px] text-center">
              {stats[item.key] || 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <p className="text-[9px] text-gray-400 font-black uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className="text-xs font-extrabold text-gray-800 break-words">
        {value || "-"}
      </p>
    </div>
  );
}

function AttendanceRecordCard({ record }) {
  const checkIn = record.check_in || record.checkin_time;
  const checkOut = record.check_out || record.checkout_time;
  const status = record.status || record.status_code;
  const workMode = record.work_mode || record.pjs_work_mode || (record.is_wfh ? "wfh" : null);
  const checkinAddress = record.checkin_address || record.check_in_address || record.address;
  const checkoutAddress = record.checkout_address || record.check_out_address;
  const lateMinutes = Number(record.late_minutes || record.late || 0);

  return (
    <div className="bg-white rounded-[20px] p-4 border border-gray-100 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-black text-gray-800">
            {record.employee_name || record.name || "Attendance Log"}
          </p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">
            {workMode || "onsite"}
          </p>
        </div>
        <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-gray-50 text-gray-500 border border-gray-100">
          {status || "recorded"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <DetailItem label="Check In" value={formatHistoryTime(checkIn)} />
        <DetailItem label="Check Out" value={formatHistoryTime(checkOut)} />
        <DetailItem label="Worked" value={formatHours(record.worked_hours)} />
        <DetailItem label="Late" value={lateMinutes > 0 ? `${lateMinutes} mnt` : "Tidak"} />
      </div>

      {(checkinAddress || checkoutAddress) && (
        <div className="mt-3 space-y-2">
          {checkinAddress && <DetailItem label="Lokasi Check In" value={checkinAddress} />}
          {checkoutAddress && <DetailItem label="Lokasi Check Out" value={checkoutAddress} />}
        </div>
      )}
    </div>
  );
}

function AttendanceDayModal({ day, onClose }) {
  if (!day) return null;

  const statusInfo = STATUS_MAP[day.status] || { title: "No Record", color: "bg-gray-300" };
  const date = new Date(`${day.date}T00:00:00`);
  const dateLabel = date.toLocaleDateString("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const isMarkedAbsent = day.status === "absent";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/40 px-4 pb-4 sm:items-center sm:pb-0">
      <button
        type="button"
        aria-label="Tutup detail absensi"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      <div className="relative w-full max-w-md rounded-[28px] bg-white p-5 shadow-2xl border border-white/80 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2.5 h-2.5 rounded-full ${statusInfo.color}`} />
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                {statusInfo.title}
              </span>
            </div>
            <h3 className="text-lg font-black text-gray-900 tracking-tight">
              {dateLabel}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 text-gray-400 hover:text-gray-700 active:scale-95"
            aria-label="Tutup modal"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {day.records.length > 0 ? (
          <div className="space-y-3">
            {day.records.map((record, index) => (
              <AttendanceRecordCard key={record.id || index} record={record} />
            ))}
          </div>
        ) : (
          <div className={`${isMarkedAbsent ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"} border rounded-2xl p-4`}>
            <p className={`text-sm font-black ${isMarkedAbsent ? "text-red-700" : "text-gray-700"}`}>
              Tidak ada log absensi
            </p>
            <p className={`text-xs mt-1 ${isMarkedAbsent ? "text-red-500" : "text-gray-500"}`}>
              {isMarkedAbsent
                ? "Sistem menandai hari ini sebagai absent karena belum ada check-in yang tercatat."
                : "Belum ada record absensi untuk tanggal ini."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const MONTHS_ID_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function ChevronDownIcon({ className }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MonthYearPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const year = value.getFullYear();
  const month = value.getMonth();
  const [viewYear, setViewYear] = useState(year);

  // Selalu buka picker pada tahun yang sedang aktif.
  useEffect(() => {
    if (open) setViewYear(year);
  }, [open, year]);

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth();
  const isFuture = (y, m) => y > curYear || (y === curYear && m > curMonth);

  const label = value.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const nextDisabled = isFuture(year, month + 1);

  const goPrev = () => onChange(new Date(year, month - 1, 1));
  const goNext = () => { if (!nextDisabled) onChange(new Date(year, month + 1, 1)); };
  const pick = (m) => { onChange(new Date(viewYear, m, 1)); setOpen(false); };

  return (
    <div className="relative">
      <div className="bg-white rounded-2xl border border-gray-100 p-2 flex items-center justify-between shadow-sm">
        <button onClick={goPrev} className="p-2 text-gray-400 hover:text-gray-800 hover:bg-gray-50 rounded-xl transition-all active:scale-95" aria-label="Bulan sebelumnya">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-gray-50 transition-all active:scale-95"
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className="text-sm font-bold text-gray-700 tracking-wide capitalize">{label}</span>
          <ChevronDownIcon className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        <button onClick={goNext} disabled={nextDisabled} className="p-2 text-gray-400 hover:text-gray-800 hover:bg-gray-50 rounded-xl transition-all active:scale-95 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed" aria-label="Bulan berikutnya">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="Tutup pemilih bulan"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 cursor-default"
          />
          <div className="absolute left-0 right-0 top-full mt-2 z-40 bg-white rounded-2xl border border-gray-100 shadow-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setViewYear((y) => y - 1)} className="p-1.5 text-gray-400 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all active:scale-95" aria-label="Tahun sebelumnya">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <span className="text-base font-black text-gray-800 tabular-nums">{viewYear}</span>
              <button onClick={() => setViewYear((y) => Math.min(y + 1, curYear))} disabled={viewYear >= curYear} className="p-1.5 text-gray-400 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all active:scale-95 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed" aria-label="Tahun berikutnya">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {MONTHS_ID_SHORT.map((m, i) => {
                const selected = i === month && viewYear === year;
                const disabled = isFuture(viewYear, i);
                return (
                  <button
                    key={m}
                    onClick={() => pick(i)}
                    disabled={disabled}
                    className={`h-10 rounded-xl text-xs font-bold transition-all active:scale-95 ${
                      selected
                        ? "bg-primary text-white shadow-sm"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    } disabled:opacity-30 disabled:hover:bg-gray-50 disabled:cursor-not-allowed`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AttendanceHistoryPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const range = getPeriodRange(currentDate);
      const data = await attendanceAPI.history({
        date_from: range.from,
        date_to: range.to,
        limit: 100,
      });

      const attendanceRecords = data?.records ?? [];
      setRecords(attendanceRecords);

      const { statsCounts } = buildAttendanceCalendar(attendanceRecords, currentDate);
      setStats(statsCounts);
    } catch (e) {
      console.error(e);
      setRecords([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Periode absensi pakai cutoff tanggal 25 (mis. periode "Agustus" = 25 Juli
  // s/d 24 Agustus), bukan awal-akhir bulan kalender biasa. Lihat getPeriodRange.
  const { start: periodStart, end: periodEnd } = getPeriodRange(currentDate);
  const firstDay = periodStart.getDay();
  const { dayMap } = buildAttendanceCalendar(records, currentDate);

  const calendarDays = [];
  const todayStr = toLocalDateKey(new Date());

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push({ day: null, isCurrentMonth: false });
  }

  for (let d = new Date(periodStart); d <= periodEnd; d.setDate(d.getDate() + 1)) {
    const dateStr = toLocalDateKey(d);
    const cell = dayMap[dateStr] || {};

    calendarDays.push({
      day: d.getDate(),
      date: dateStr,
      isCurrentMonth: true,
      isAdjacentMonth: d.getMonth() !== currentDate.getMonth(),
      status: cell.status,
      flags: cell.flags || {},
      records: cell.records || [],
      isToday: todayStr === dateStr,
    });
  }

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const periodLabel = `${periodStart.getDate()} ${MONTHS_ID_SHORT[periodStart.getMonth()]} – ${periodEnd.getDate()} ${MONTHS_ID_SHORT[periodEnd.getMonth()]} ${periodEnd.getFullYear()}`;

  return (
    <div className="min-h-screen bg-[#FDFEFE] text-gray-900 pb-16 antialiased relative overflow-hidden">
      <div className="absolute top-[30%] -left-40 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-[60%] -right-40 w-96 h-96 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />

      <GreetingHeader />

      <main className="max-w-md mx-auto px-6 -mt-28 relative z-20 bg-white rounded-t-[40px] pt-8 flex flex-col gap-5 shadow-[0_-12px_30px_rgba(0,0,0,0.03)] pb-10">
        {loading ? (
          <div className="py-20 bg-white rounded-[28px] border border-gray-100 shadow-sm flex justify-center">
            <PageLoader />
          </div>
        ) : (
          <>
            <div>
              <MonthYearPicker value={currentDate} onChange={setCurrentDate} />
              <p className="text-center text-[11px] font-semibold text-gray-400 mt-2">
                Periode {periodLabel}
              </p>
            </div>

            <div className="bg-white rounded-[28px] p-5 border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="relative z-10">
                <div className="grid grid-cols-7 gap-1 mb-3">
                  {weekDays.map((day) => (
                    <div key={day} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {calendarDays.map((item, idx) => (
                    <DayCell
                      key={idx}
                      day={item.day}
                      status={item.status}
                      flags={item.flags}
                      isCurrentMonth={item.isCurrentMonth}
                      isAdjacentMonth={item.isAdjacentMonth}
                      isToday={item.isToday}
                      onClick={() => setSelectedDay(item)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <SummaryStats stats={stats} />
          </>
        )}
      </main>

      <AttendanceDayModal day={selectedDay} onClose={() => setSelectedDay(null)} />
    </div>
  );
}
